import { createApi } from '@reduxjs/toolkit/query/react';

import {
  getBaseQuery,
  TransformedErrorResponseWithNotification,
  TransformedResponseWithNotification,
  getListProvidesTags,
} from '../generic';
import { HTTP_METHOD, REDUX_CACHE } from '../constants';
import * as endpoints from '../endpoints';

export const SYNC_PROVIDER_WORKOUT_API = 'workout_api';

export interface SyncProviderOptionDto {
  provider: string;
  entities: string[];
}

export interface MuscleSyncItemDto {
  providerResourceId: string;
  name: string;
  localResourceId?: string;
  provider: string;
  entity: string;
  code: string;
  raw: any;
}

export interface ExerciseSyncItemDto {
  providerResourceId: string;
  name: string;
  localResourceId?: string;
  provider: string;
  entity: string;
  raw: any;
}

export interface SyncMappingDto {
  id: string;
  provider: string;
  entity: string;
  providerResourceId: string;
  localResourceId: string;
  meta?: string;
}

export interface SyncExerciseFileToUploadDto {
  id: string | number;
  name: string;
  mimeType: string;
  storageLink?: string;
  previewStorageLink?: string;
  size?: number;
}

export interface SyncExerciseCreateDto {
  title: string;
  description?: string;
  categoryId: string;
  mainMuscleGroupId: string;
  equipmentId: string;
  additionalMuscleIds?: string[];
  additionalEquipmentIds?: string[];
  developedSkillIds?: string[];
  exerciseLoadFormulaId?: string;
  splitBySide?: boolean;
  languageId: string;
  optionIds?: string[];
  filesToUpload?: SyncExerciseFileToUploadDto[];
  avatarFileId?: string | number;
}

export interface SyncExerciseRequestDto {
  providerResourceId: string;
  localResourceId?: string;
  create?: SyncExerciseCreateDto;
}

export interface SyncExerciseResultDto {
  providerResourceId: string;
  localResourceId: string;
}

export interface SyncMuscleCreateDto {
  nameKey: string;
  type?: string;
  parentId?: string;
  priority?: number;
}

export interface SyncMuscleRequestDto {
  providerResourceId: string;
  localResourceId?: string;
  create?: SyncMuscleCreateDto;
}

export interface SyncMuscleResultDto {
  providerResourceId: string;
  localResourceId: string;
}

export type SyncRuleTransform = 'none' | 'toLowerCase' | 'toUpperCase';

export interface SyncRuleDto {
  id: string;
  provider: string;
  entity: string;
  sourcePath?: string | null;
  defaultValue?: string | null;
  localPath: string;
  prefix?: string | null;
  transform?: SyncRuleTransform | null;
}

export type SyncRuleCreateDto = Omit<SyncRuleDto, 'id'>;
export type SyncRuleUpdateDto = Partial<SyncRuleCreateDto>;

/** Provider sync (lists + create/link) can run long; avoid client TIMEOUT_ERROR on slow networks. */
const SYNC_API_TIMEOUT_MS = 30 * 60 * 1000;

export const syncApi = createApi({
  reducerPath: 'syncApi',
  baseQuery: getBaseQuery('', { timeout: SYNC_API_TIMEOUT_MS }),
  keepUnusedDataFor: 60,
  tagTypes: ['SYNC_RULES', REDUX_CACHE.ABORT_INVALIDATION, REDUX_CACHE.LIST],
  endpoints: (builder) => ({
    getSyncProviders: builder.query<SyncProviderOptionDto[], void>({
      keepUnusedDataFor: 3600,
      query() {
        return {
          url: endpoints.Sync.PROVIDERS,
          method: HTTP_METHOD.GET,
        };
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    getMusclesForSync: builder.query<MuscleSyncItemDto[], string>({
      query(provider) {
        return {
          url: `${endpoints.Sync.MUSCLES}?provider=${encodeURIComponent(
            provider,
          )}`,
          method: HTTP_METHOD.GET,
        };
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    syncMuscle: builder.mutation<
      SyncMuscleResultDto,
      { provider: string; body: SyncMuscleRequestDto }
    >({
      query({ provider, body }) {
        return {
          url: `${endpoints.Sync.MUSCLES}?provider=${encodeURIComponent(
            provider,
          )}`,
          method: HTTP_METHOD.POST,
          body,
        };
      },
      ...TransformedErrorResponseWithNotification(),
    }),

    getExercisesForSync: builder.query<ExerciseSyncItemDto[], string>({
      query(provider) {
        return {
          url: `${endpoints.Sync.EXERCISES}?provider=${encodeURIComponent(
            provider,
          )}`,
          method: HTTP_METHOD.GET,
        };
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    syncExercise: builder.mutation<
      SyncExerciseResultDto,
      { provider: string; body: SyncExerciseRequestDto }
    >({
      query({ provider, body }) {
        return {
          url: `${endpoints.Sync.EXERCISES}?provider=${encodeURIComponent(
            provider,
          )}`,
          method: HTTP_METHOD.POST,
          body,
        };
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    getMappings: builder.query<
      SyncMappingDto[],
      { provider?: string; entity?: string }
    >({
      keepUnusedDataFor: 60,
      query({ provider, entity }) {
        const params = new URLSearchParams();
        if (provider) params.set('provider', provider);
        if (entity) params.set('entity', entity);
        const qs = params.toString();
        return {
          url: qs
            ? `${endpoints.Sync.MAPPINGS}?${qs}`
            : endpoints.Sync.MAPPINGS,
          method: HTTP_METHOD.GET,
        };
      },
      ...TransformedErrorResponseWithNotification(),
    }),

    getSyncRules: builder.query<SyncRuleDto[], void>({
      keepUnusedDataFor: 3600,
      query() {
        return {
          url: endpoints.Sync.RULES,
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result) => getListProvidesTags(result, 'SYNC_RULES'),
      ...TransformedErrorResponseWithNotification(),
    }),
    getSyncRuleById: builder.query<SyncRuleDto, string>({
      keepUnusedDataFor: 3600,
      query(id: string) {
        return {
          url: `${endpoints.Sync.RULES}/${id}`,
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result, error, id) => [{ type: 'SYNC_RULES', id }],
      ...TransformedErrorResponseWithNotification(),
    }),
    createOrUpdateSyncRule: builder.mutation<
      SyncRuleDto,
      { id?: string; body: SyncRuleCreateDto | SyncRuleUpdateDto }
    >({
      query({ id, body }) {
        return {
          url: id ? `${endpoints.Sync.RULES}/${id}` : endpoints.Sync.RULES,
          method: id ? HTTP_METHOD.PUT : HTTP_METHOD.POST,
          body,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        }

        const tags: any[] = [{ type: 'SYNC_RULES', id: REDUX_CACHE.LIST }];

        if (params.id) {
          tags.push({ type: 'SYNC_RULES', id: params.id });
        }

        return tags;
      },
      ...TransformedResponseWithNotification(),
    }),
    deleteSyncRule: builder.mutation<void, string>({
      query(id: string) {
        return {
          url: `${endpoints.Sync.RULES}/${id}`,
          method: HTTP_METHOD.DELETE,
        };
      },
      invalidatesTags: (result, error, id) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        }

        return [
          { type: 'SYNC_RULES', id: REDUX_CACHE.LIST },
          { type: 'SYNC_RULES', id },
        ];
      },
      ...TransformedResponseWithNotification(),
    }),
  }),
});

export const {
  useGetSyncProvidersQuery: useGetSyncProviders,
  useLazyGetMusclesForSyncQuery,
  useSyncMuscleMutation,
  useLazyGetExercisesForSyncQuery,
  useSyncExerciseMutation,
  useGetMappingsQuery,
  useLazyGetMappingsQuery,
  useGetSyncRulesQuery: useGetSyncRules,
  useGetSyncRuleByIdQuery: useGetSyncRuleById,
  useCreateOrUpdateSyncRuleMutation: useCreateOrUpdateSyncRule,
  useDeleteSyncRuleMutation: useDeleteSyncRule,
} = syncApi;
